import { ThemeProvider } from "styled-components";
import { token } from "../../theme";

import { useState, useEffect, useRef, useCallback } from "react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";

import { getWebProjectItems } from "../../backend/hygraph/hygraph";
import {
  CRTOverlay,
  HomePageContainer,
  HomePageWindowContainer,
} from "./HomePageStyles";
import HomeBottomBar from "./components/BottomBar/HomeBottomBar";
import HomeBackgroundIcons from "./components/BackgroundIcons/HomeBackgroundIcons";
import { DESKTOP_APPS } from "./components/BackgroundIcons/desktopApps";
import { useWindowManager } from "./utils/WindowManager";
import HomeDesktop from "./components/Desktop/HomeDesktop";
import { WebProjectItemModel } from "./utils/types";
import ProjectList from "./components/ProjectList/ProjectList";
import ItemsFolder from "../../components/ItemsFolder/ItemsFolder";
import ModalContent from "./components/ModalContent/ModalContent";
import { calculateCenterPosition } from "./utils/utils";
import BootSequence from "../../components/BootSequence/BootSequence";
import ShutdownScreen from "../../components/ShutdownScreen/ShutdownScreen";
import DesktopSelection from "../../components/DesktopSelection/DesktopSelection";
import ClickSpark from "../../components/Effects/ClickSpark";
import { isMuted, setMuted } from "../../utils/sound";
import { DesktopProvider, LoadStatus } from "./utils/DesktopContext";
import { WallpaperId, getWallpaper } from "../../config/wallpapers";
import DesktopContextMenu from "../../components/DesktopContextMenu/DesktopContextMenu";
import Screensaver from "../../components/Screensaver/Screensaver";

type Stage = "boot" | "desktop" | "shutdown";

const BOOTED_KEY = "desktop-booted";
const CRT_KEY = "desktop-crt";
const WALLPAPER_KEY = "desktop-wallpaper";

const readStorage = (storage: () => Storage, key: string) => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: () => Storage, key: string, value: string) => {
  try {
    storage().setItem(key, value);
  } catch {
    // storage unavailable (private mode etc), nothing to persist
  }
};

const HomePage = () => {
  const [showHomeMainWindow] = useState<boolean>(true);

  // Only play the full intro once per browser session, refreshes go straight to the desktop
  const [stage, setStage] = useState<Stage>(() =>
    readStorage(() => sessionStorage, BOOTED_KEY) ? "desktop" : "boot"
  );
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("loading");
  const [soundOn, setSoundOn] = useState(() => !isMuted());
  const [crtOn, setCrtOn] = useState(
    () => readStorage(() => localStorage, CRT_KEY) !== "0"
  );
  const [wallpaper, setWallpaperState] = useState<WallpaperId>(
    () =>
      getWallpaper(
        (readStorage(() => localStorage, WALLPAPER_KEY) ||
          "bliss") as WallpaperId
      ).id
  );
  // bumping this remounts the icons so they replay their entrance
  const [desktopKey, setDesktopKey] = useState(0);

  const [mainWebProjectItems, setMainWebProjectItems] = useState<
    WebProjectItemModel[]
  >([]);
  const [otherWebProjectItems, setOtherWebProjectItems] = useState<
    WebProjectItemModel[]
  >([]);

  const tutorialOpenedRef = useRef(false);

  const { openWindow, closeWindow, closeAllWindows } = useWindowManager();

  useEffect(() => {
    const fetchPortfolioItems = async () => {
      try {
        const response: WebProjectItemModel[] = await getWebProjectItems();
        //sort unimportant and important
        const mainWebProjects = response.filter((project) => project.important);
        const otherWebProjects = response.filter(
          (project) => !project.important
        );

        setMainWebProjectItems(mainWebProjects);
        setOtherWebProjectItems(otherWebProjects);
        setLoadStatus("ready");
      } catch (error) {
        console.error("Error fetching portfolio items:", error);
        setLoadStatus("error");
      }
    };

    fetchPortfolioItems();
  }, []);

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  const openTutorialWindow = () => {
    if (!tutorialOpenedRef.current) {
      tutorialOpenedRef.current = true;
      const centerPosition = calculateCenterPosition(400, 300);
      const windowId = openWindow({
        position: centerPosition,
        isMinimized: false,
        isMaximized: false,
        component: (
          <ModalContent
            onActionButtonClick={() => {
              closeWindow(windowId);
            }}
            content="Welcome to my desktop. Double click on any icon to show its contents or right click for more information. Try the Start menu too. Have fun!"
          />
        ),
        windowTitle: "Tutorial",
      });
    }
  };

  // Let the icons finish popping in before the tutorial window lands
  useEffect(() => {
    if (stage !== "desktop") return;
    const timeout = setTimeout(openTutorialWindow, 1100);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleBootComplete = useCallback(() => {
    writeStorage(() => sessionStorage, BOOTED_KEY, "1");
    setStage("desktop");
  }, []);

  const handleShutDownRequest = () => {
    const windowId = openWindow({
      position: calculateCenterPosition(400, 200),
      isMinimized: false,
      isMaximized: false,
      component: (
        <ModalContent
          title="Shut Down"
          buttonsType="action"
          content="Are you sure you want to shut down the computer?"
          onActionButtonClick={() => {
            closeAllWindows();
            setStage("shutdown");
          }}
          onOtherButtonClick={() => closeWindow(windowId)}
        />
      ),
      windowTitle: "Shut Down Windows",
    });
  };

  const handleRestart = () => {
    tutorialOpenedRef.current = false;
    setStage("boot");
  };

  const toggleSound = () => {
    setMuted(soundOn);
    setSoundOn(!soundOn);
  };

  const toggleCrt = () => {
    writeStorage(() => localStorage, CRT_KEY, crtOn ? "0" : "1");
    setCrtOn(!crtOn);
  };

  const setWallpaper = (id: WallpaperId) => {
    writeStorage(() => localStorage, WALLPAPER_KEY, id);
    setWallpaperState(id);
  };

  const desktopValue = {
    projects: [...mainWebProjectItems, ...otherWebProjectItems],
    loadStatus,
    wallpaper,
    setWallpaper,
    soundOn,
    toggleSound,
    crtOn,
    toggleCrt,
    refreshDesktop: () => setDesktopKey((key) => key + 1),
    shutDown: handleShutDownRequest,
  };

  const projectTitles = desktopValue.projects.map((project) => project.title);

  return (
    <ThemeProvider theme={token}>
      <DesktopProvider value={desktopValue}>
        <MotionConfig reducedMotion="user">
          <ClickSpark sparkColor="#ffd84a">
            <HomePageContainer
              $background={getWallpaper(wallpaper).background}
              data-desktop-surface
            >
              <HomePageWindowContainer
                show={showHomeMainWindow}
              ></HomePageWindowContainer>

              {stage !== "boot" && (
                <>
                  <HomeDesktop />

                  <HomeBackgroundIcons key={`icons-${desktopKey}`} />

                  <ProjectList
                    key={`projects-${desktopKey}`}
                    webProjectItems={mainWebProjectItems}
                    staggerOffset={DESKTOP_APPS.length}
                  />
                  <ItemsFolder
                    key={`folder-${desktopKey}`}
                    component={
                      <ProjectList webProjectItems={otherWebProjectItems} />
                    }
                    folderName="Other Projects"
                    showBottomBar={true}
                    staggerIndex={
                      DESKTOP_APPS.length + mainWebProjectItems.length
                    }
                  />

                  <HomeBottomBar />
                  <DesktopSelection />
                  <DesktopContextMenu />
                  <Screensaver />
                </>
              )}

              {crtOn && <CRTOverlay />}
            </HomePageContainer>

            <AnimatePresence>
              {stage === "boot" && (
                <motion.div
                  key="boot"
                  exit={{ opacity: 0, scale: 1.04 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  style={{ position: "fixed", inset: 0, zIndex: 200000 }}
                >
                  <BootSequence
                    projectTitles={projectTitles}
                    status={loadStatus}
                    onComplete={handleBootComplete}
                  />
                </motion.div>
              )}
              {stage === "shutdown" && (
                <ShutdownScreen key="shutdown" onRestart={handleRestart} />
              )}
            </AnimatePresence>
          </ClickSpark>
        </MotionConfig>
      </DesktopProvider>
    </ThemeProvider>
  );
};
export default HomePage;
